import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  useMediaQuery,
} from "@material-ui/core";
import LaunchIcon from "@material-ui/icons/Launch";
import { useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getInvoicesByUser } from "../../actions/invoiceActions";

const currencyFmt = (v, code = "USD") =>
  new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: code,
    maximumFractionDigits: 0,
  }).format(v || 0);

const formatDate = (value) => {
  if (!value) return "No date";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(+date)) return "No date";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const statusColor = (status) => {
  const normalized = (status || "").toLowerCase();
  if (normalized === "paid") return "#047857";
  if (normalized === "partial") return "#b45309";
  if (normalized === "unpaid") return "#b91c1c";
  return "#4b5563";
};

export default function CustomerInvoicesCard({
  title = "Invoices by Account",
  currency = "USD",
  autoLoad = true,
  invoices: preloadedInvoices,
  loading,
}) {
  const history = useHistory();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery("(max-width:600px)");
  const { invoices = [], isLoading: storeIsLoading = false } = useSelector(
    (state) => state?.invoices || {}
  );
  const sourceInvoices = Array.isArray(preloadedInvoices) ? preloadedInvoices : invoices;
  const isLoading = typeof loading === "boolean" ? loading : storeIsLoading;

  const userInfo = React.useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const stored = JSON.parse(localStorage.getItem("profile") || "null");
      return stored?.result || stored;
    } catch {
      return null;
    }
  }, []);

  React.useEffect(() => {
    if (Array.isArray(preloadedInvoices)) return;
    if (!autoLoad) return;
    const id = userInfo?._id || userInfo?.googleId;
    if (!id) return;
    if (invoices?.length) return;
    dispatch(getInvoicesByUser({ search: id }));
  }, [autoLoad, dispatch, invoices?.length, preloadedInvoices, userInfo]);

  const customerOptions = React.useMemo(() => {
    const set = new Set();
    (sourceInvoices || []).forEach((inv) => {
      const account = (inv?.billTo?.name || inv?.billTo?.store || inv?.client?.store || "").trim();
      if (account) set.add(account);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [sourceInvoices]);

  const [customer, setCustomer] = React.useState("");

  React.useEffect(() => {
    if (!customerOptions.length) {
      setCustomer("");
      return;
    }
    if (!customer || !customerOptions.includes(customer)) {
      setCustomer(customerOptions[0]);
    }
  }, [customerOptions, customer]);

  const filteredInvoices = React.useMemo(() => {
    if (!customer) return [];
    return (sourceInvoices || [])
      .filter((inv) => {
        const account = (inv?.billTo?.name || inv?.billTo?.store || inv?.client?.store || "").trim();
        return account === customer;
      })
      .sort(
        (a, b) =>
          new Date(b?.createdAt || b?.dueDate || 0) -
          new Date(a?.createdAt || a?.dueDate || 0)
      );
  }, [sourceInvoices, customer]);

  return (
    <Card variant="outlined" style={{ height: "100%" }}>
      <CardHeader
        title={title}
        action={
          customerOptions.length ? (
            <FormControl
              variant="outlined"
              size="small"
              style={{ minWidth: isMobile ? 0 : 200, width: isMobile ? "100%" : undefined }}
            >
              <InputLabel>Account</InputLabel>
              <Select
                label="Account"
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
              >
                {customerOptions.map((name) => (
                  <MenuItem key={name} value={name}>
                    {name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : null
        }
      />
      <CardContent style={{ maxHeight: 320, overflowY: "auto" }}>
        {isLoading ? (
          <Typography variant="body2" color="textSecondary">
            Loading invoices...
          </Typography>
        ) : !customerOptions.length ? (
          <Typography variant="body2" color="textSecondary">
            No customers with invoices found.
          </Typography>
        ) : filteredInvoices.length === 0 ? (
          <Typography variant="body2" color="textSecondary">
            No invoices for {customer}.
          </Typography>
        ) : (
          filteredInvoices.map((inv) => (
            <Box
              key={inv._id}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              py={0.75}
              borderBottom="1px solid rgba(0,0,0,0.06)"
              style={{ cursor: "pointer", gap: 12, minWidth: 0 }}
              onClick={() => history.push(`/invoice/${inv._id}`)}
            >
              <Box style={{ minWidth: 0, flex: "1 1 auto" }}>
                <Typography variant="body2" style={{ fontWeight: 700 }}>
                  {inv.invoiceNumber ? `Invoice #${inv.invoiceNumber}` : "Invoice"}
                </Typography>
                <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                  {inv.receivedByDate ? `Ship Date: ${formatDate(inv.receivedByDate)}` : `Created: ${formatDate(inv.createdAt)}`}
                </Typography>
                <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                  {inv.dueDate ? `Due Date: ${formatDate(inv.dueDate)}` : ""}
                </Typography>
                <Typography variant="caption" color="textSecondary" style={{ display: "block" }}>
                  {(inv.items || []).slice(0, 3).map((item, idx) => (
                    <span key={idx}>
                      {item?.modelNumber || item?.description || "Item"}
                      {idx < Math.min(inv.items.length, 3) - 1 ? ", " : ""}
                    </span>
                  ))}
                  {inv.items?.length > 3 ? ` (+${inv.items.length - 3} more)` : ""}
                </Typography>
              </Box>
              <Box
                textAlign="right"
                style={{ flex: "0 0 auto" }}
              >
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {currencyFmt(inv.total, currency)}
                </Typography>
                <Chip
                  size="small"
                  label={inv.status || "Unknown"}
                  style={{
                    marginTop: 4,
                    backgroundColor: statusColor(inv.status),
                    color: "#fff",
                  }}
                />
                <Box display="flex" alignItems="center" justifyContent="flex-end" mt={0.5} style={{ gap: 4 }}>
                  <Typography variant="caption" color="primary">
                    View Invoice
                  </Typography>
                  <IconButton size="small" style={{ padding: 0 }}>
                    <LaunchIcon fontSize="small" color="primary" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          ))
        )}
      </CardContent>
    </Card>
  );
}
